import { useState, useEffect } from 'react';
import { getActiveCategories, Category } from '../services/categoryService';
import { categories as defaultCategories } from '../data/events';
import { useTranslation } from 'react-i18next';

export const useCategories = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { i18n } = useTranslation();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const fetchedCategories = await getActiveCategories();
        
        if (fetchedCategories.length > 0) {
          setCategories(fetchedCategories);
        } else {
          // If no categories in Firestore, create default categories from static data
          const defaultCats = defaultCategories.map((cat, index) => ({
            id: cat,
            name: cat,
            nameEn: cat,
            namePt: cat,
            slug: cat,
            order: index,
            active: true
          }));
          setCategories(defaultCats);
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
        setError('Failed to load categories');
        
        // Fallback to default categories
        const defaultCats = defaultCategories.map((cat, index) => ({
          id: cat,
          name: cat,
          nameEn: cat,
          namePt: cat,
          slug: cat,
          order: index,
          active: true
        }));
        setCategories(defaultCats);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const PT_FALLBACK: Record<string, string> = {
    parties: 'Festas', party: 'Festas',
    concert: 'Shows', concerts: 'Shows', music: 'Música',
    sports: 'Esportes', sport: 'Esportes',
    theater: 'Teatro', theatre: 'Teatro',
    conference: 'Conferências', conferences: 'Conferências',
    festival: 'Festivais', festivals: 'Festivais',
    other: 'Outros',
    arts: 'Arte', art: 'Arte',
    technology: 'Tecnologia', tech: 'Tecnologia',
    food: 'Gastronomia', gastronomy: 'Gastronomia',
    dance: 'Dança',
    comedy: 'Comédia',
  };

  // Get localized category name based on current language
  const getCategoryName = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId || c.slug === categoryId);
    if (!category) {
      if (i18n.language === 'pt') return PT_FALLBACK[categoryId.toLowerCase()] ?? categoryId;
      return categoryId;
    }

    const currentLanguage = i18n.language;
    if (currentLanguage === 'pt') {
      return category.namePt || PT_FALLBACK[category.slug?.toLowerCase()] || PT_FALLBACK[category.name?.toLowerCase()] || category.name;
    }
    return category.nameEn || category.name;
  };

  return { categories, loading, error, getCategoryName };
};